import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";

/**
 * A skeleton loader for the individual Ramp detail page
 * This displays placeholder elements with animations while the real data is loading
 */
export const RampSkeleton: React.FC = () => {
  return (
    <Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
      <Stack direction="column" sx={{ height: "100%" }}>
        {/* Header with action buttons */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Skeleton variant="text" width={250} height={40} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Skeleton 
              variant="rounded" 
              width={120} 
              height={40} 
              sx={{ animation: "pulse 1.5s ease-in-out 0.5s infinite" }}
            />
            <Skeleton 
              variant="rounded" 
              width={120} 
              height={40} 
              sx={{ animation: "pulse 1.5s ease-in-out 0.6s infinite" }}
            />
          </Stack>
        </Stack>

        {/* Main content area */}
        <Stack 
          direction="row" 
          sx={{ 
            height: "calc(100% - 64px)", 
            overflow: "hidden" 
          }}
        >
          {/* Left panel - Info card */}
          <Stack 
            direction="column" 
            spacing={2} 
            sx={{ 
              width: 300, 
              p: 2, 
              borderRight: "1px solid rgba(0, 0, 0, 0.12)" 
            }}
          >
            {/* Ramp info card */}
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">
                    <Skeleton width={150} />
                  </Typography>
                  <Divider />
                  
                  {/* Ramp dimensions */}
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        <Skeleton width={80} />
                      </Typography>
                      <Typography variant="body2">
                        <Skeleton width={60} />
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        <Skeleton width={80} />
                      </Typography>
                      <Typography variant="body2">
                        <Skeleton width={60} />
                      </Typography>
                    </Stack>
                  </Stack>
                  
                  <Divider />
                  
                  {/* Aircraft count */}
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">
                      <Skeleton width={120} />
                    </Typography>
                    <Typography variant="body2">
                      <Skeleton width={40} />
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
            
            {/* Current aircraft section */}
            <Typography variant="subtitle1">
              <Skeleton width={150} />
            </Typography>
            
            {/* Aircraft list */}
            <Stack spacing={1}>
              {[...Array(5)].map((_, index) => (
                <Card key={`aircraft-skeleton-${index}`} variant="outlined">
                  <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Skeleton 
                        variant="rectangular" 
                        width={40} 
                        height={40} 
                        sx={{ 
                          borderRadius: 1,
                          animation: `pulse 1.5s ease-in-out ${0.5 + index * 0.1}s infinite` 
                        }}
                      />
                      <Stack>
                        <Typography variant="body2">
                          <Skeleton width={120} />
                        </Typography>
                        <Typography variant="caption">
                          <Skeleton width={80} />
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Stack>
          
          {/* Right panel - Ramp layout area */}
          <Box 
            sx={{ 
              flex: 1, 
              position: "relative", 
              backgroundColor: "#f5f5f5", 
              overflow: "hidden" 
            }}
          >
            {/* Ramp background */}
            <Skeleton 
              variant="rectangular" 
              width="100%" 
              height="100%" 
              sx={{ 
                position: "absolute",
                animation: "pulse 1.5s ease-in-out 0.3s infinite",
                opacity: 0.7
              }}
            />
            
            {/* Ramp markings placeholders */}
            <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
              {/* Horizontal markings */}
              {[...Array(5)].map((_, index) => (
                <Skeleton 
                  key={`h-marking-${index}`}
                  variant="rectangular" 
                  width="80%" 
                  height={2} 
                  sx={{ 
                    position: "absolute",
                    top: `${15 + index * 20}%`,
                    left: "10%",
                    animation: `pulse 1.5s ease-in-out ${0.3 + index * 0.1}s infinite`,
                    opacity: 0.5
                  }}
                />
              ))}
              
              {/* Vertical markings */}
              {[...Array(4)].map((_, index) => (
                <Skeleton 
                  key={`v-marking-${index}`}
                  variant="rectangular" 
                  width={2} 
                  height="70%" 
                  sx={{ 
                    position: "absolute",
                    top: "15%",
                    left: `${20 + index * 20}%`,
                    animation: `pulse 1.5s ease-in-out ${0.3 + index * 0.1}s infinite`,
                    opacity: 0.5
                  }}
                />
              ))}
              
              {/* Aircraft placeholder shapes */}
              {[...Array(3)].map((_, index) => (
                <Skeleton 
                  key={`aircraft-shape-${index}`}
                  variant="rounded" 
                  width={120} 
                  height={60} 
                  sx={{ 
                    position: "absolute",
                    top: `${20 + index * 25}%`,
                    left: `${25 + index * 15}%`,
                    animation: `pulse 1.5s ease-in-out ${0.5 + index * 0.2}s infinite`,
                    opacity: 0.6,
                    transform: `rotate(${index * 30}deg)`
                  }}
                />
              ))}
            </Box>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};
